/**
 * Iter 127 — Court Order create dialog.
 *
 * Order-first architecture: this dialog captures ONLY the order
 * shell (subject, name, jurisdiction, court, judge, reference,
 * effective dates, expiry, review, child scope, notes, activate).
 * Sections are added one at a time afterwards from the detail
 * panel — so a Court Order can carry any combination of parenting,
 * contact, financial, welfare and other sections rather than being
 * forced into a single financial-only model.
 *
 * v77.99.132 changes:
 *   * REQUIRED "Order type" (subject) dropdown mirroring the
 *     COURT_ORDER_SUBJECTS taxonomy. "Other" surfaces a required
 *     free-text field.
 *   * "Effective to" date with "No expiry" checkbox.
 *   * Optional "Review date".
 *   * Optional "Supersedes existing order" picker — the caller
 *     picks the specific same-subject prior order to close. Default
 *     behaviour NO LONGER auto-supersedes unrelated orders.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import {
  COUNTRY_JURISDICTIONS,
  ORDER_SUBJECTS,
} from "@/components/courtOrders/courtOrderTypes";

const COURT_PLACEHOLDERS = {
  ie: "e.g. Naas District Court, Dublin Circuit Family Court",
  ni: "e.g. Belfast Family Court",
  ew: "e.g. Central Family Court, High Court (Family Division)",
  sc: "e.g. Edinburgh Sheriff Court",
  other: "Court or tribunal name",
  "": "Court or tribunal name",
};

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function blankForm() {
  return {
    subject: "",
    subject_other: "",
    name: "",
    reference: "",
    country_jurisdiction: "",
    issuing_court: "",
    judge: "",
    effective_from: todayISO(),
    effective_to: "",
    no_expiry: false,
    review_date: "",
    notes: "",
    all_children: true,
    child_ids: [],
    activate_now: true,
    supersedes_order_id: "",
    supersede_note: "",
  };
}

export function CourtOrderCreateDialog({ open, onOpenChange, relationship, onCreated }) {
  const [form, setForm] = useState(blankForm());
  const [busy, setBusy] = useState(false);
  const [existingOrders, setExistingOrders] = useState([]);

  useEffect(() => {
    if (open) {
      setForm(blankForm());
    }
  }, [open]);

  // Iter 127 — Fetch existing orders on this relationship so the
  // "supersedes" picker can be constrained to the SAME subject.
  useEffect(() => {
    if (!open || !relationship?.id) return;
    api.get(`/court-orders?relationship_id=${relationship.id}`)
      .then((r) => setExistingOrders(r.data?.orders || []))
      .catch(() => setExistingOrders([]));
  }, [open, relationship?.id]);

  const supersedeCandidates = useMemo(() => {
    if (!form.subject) return [];
    return existingOrders.filter(
      (o) => (o.subject || "").toLowerCase() === form.subject && ["active", "draft", "scheduled"].includes(o.status),
    );
  }, [existingOrders, form.subject]);

  const subjectMeta = useMemo(
    () => ORDER_SUBJECTS.find((s) => s.value === form.subject) || null,
    [form.subject],
  );

  if (!relationship) return null;

  const toggleChild = (cid) => {
    setForm((f) => {
      const next = new Set(f.child_ids);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return { ...f, child_ids: Array.from(next) };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject) {
      toast.error("Please choose an order type.");
      return;
    }
    if (form.subject === "other" && !form.subject_other.trim()) {
      toast.error("Please describe the order type when selecting 'Other'.");
      return;
    }
    if (!form.name.trim()) {
      toast.error("Please give the order a name.");
      return;
    }
    if (!form.country_jurisdiction) {
      toast.error("Please choose a country / jurisdiction.");
      return;
    }
    if (!form.all_children && form.child_ids.length === 0) {
      toast.error("Pick at least one child or switch back to 'All children'.");
      return;
    }
    if (subjectMeta?.expiring && !form.effective_to && !form.no_expiry) {
      toast.error("Safety-type orders require either an expiry date or an explicit 'No expiry' declaration.");
      return;
    }
    if (form.no_expiry && form.effective_to) {
      toast.error("Uncheck 'No expiry' or clear the effective-to date — you can't have both.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        relationship_id: relationship.id,
        name: form.name.trim(),
        reference: form.reference.trim(),
        subject: form.subject,
        subject_other: form.subject === "other" ? form.subject_other.trim() : null,
        country_jurisdiction: form.country_jurisdiction,
        issuing_court: form.issuing_court.trim(),
        judge: form.judge.trim(),
        all_children: form.all_children,
        child_ids: form.all_children ? [] : form.child_ids,
        effective_from: form.effective_from,
        effective_to: form.no_expiry ? null : (form.effective_to || null),
        no_expiry: form.no_expiry,
        review_date: form.review_date || null,
        notes: form.notes.trim(),
        sections: [],
        activate_now: form.activate_now,
        supersedes_order_id: form.supersedes_order_id || null,
        supersede_note: form.supersedes_order_id ? (form.supersede_note.trim() || null) : null,
      };
      const r = await api.post("/court-orders", body);
      onOpenChange(false);
      onCreated && onCreated(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save order.");
    } finally {
      setBusy(false);
    }
  };

  const placeholder = COURT_PLACEHOLDERS[form.country_jurisdiction] || COURT_PLACEHOLDERS[""];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white border-[#D9E1E3] max-h-[90vh] overflow-y-auto"
        data-testid="co-create-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">New court order</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-[12px] text-[#6F828A] -mt-1">
            For relationship:{" "}
            <span className="text-[#0B2E32] font-medium">{relationship.label}</span>
            {" — "}multiple concurrent orders are allowed. A new order
            only replaces another when you explicitly pick one below.
          </p>

          <div>
            <Label>Order type <span className="text-[#7A2E24]">*</span></Label>
            <Select
              value={form.subject || undefined}
              onValueChange={(v) => setForm({ ...form, subject: v, subject_other: "", supersedes_order_id: "" })}
            >
              <SelectTrigger data-testid="co-create-subject">
                <SelectValue placeholder="Choose an order type…" />
              </SelectTrigger>
              <SelectContent>
                {ORDER_SUBJECTS.map((s) => (
                  <SelectItem key={s.value} value={s.value} data-testid={`co-create-subject-${s.value}`}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {subjectMeta?.expiring ? (
              <p className="text-[11px] text-[#7A2E24] mt-1">
                Statutory expiry required — set an effective-to date below or tick “No expiry”.
              </p>
            ) : null}
            {form.subject === "other" ? (
              <Input
                value={form.subject_other}
                onChange={(e) => setForm({ ...form, subject_other: e.target.value })}
                placeholder="Describe the order type (required)"
                className="mt-2"
                data-testid="co-create-subject-other"
                required
              />
            ) : null}
          </div>

          <div>
            <Label>Order name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. 2024 Family Court Order"
              required
              data-testid="co-create-name"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Country / jurisdiction</Label>
              <Select
                value={form.country_jurisdiction || undefined}
                onValueChange={(v) => setForm({ ...form, country_jurisdiction: v })}
              >
                <SelectTrigger data-testid="co-create-country">
                  <SelectValue placeholder="Choose…" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_JURISDICTIONS.map((j) => (
                    <SelectItem key={j.value} value={j.value}>
                      {j.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#6F828A] mt-1">
                Drives the legal framework for compliance reporting.
              </p>
            </div>
            <div>
              <Label>Court</Label>
              <Input
                value={form.issuing_court}
                onChange={(e) => setForm({ ...form, issuing_court: e.target.value })}
                placeholder={placeholder}
                data-testid="co-create-court"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Judge (optional)</Label>
              <Input
                value={form.judge}
                onChange={(e) => setForm({ ...form, judge: e.target.value })}
                placeholder="Hon. ___"
                data-testid="co-create-judge"
              />
            </div>
            <div>
              <Label>Court reference (optional)</Label>
              <Input
                value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                placeholder="e.g. FC/2024/001"
                data-testid="co-create-reference"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Effective from</Label>
              <Input
                type="date"
                value={form.effective_from}
                onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                required
                data-testid="co-create-effective-from"
              />
            </div>
            <div>
              <Label>Effective to (expiry)</Label>
              <Input
                type="date"
                value={form.effective_to}
                onChange={(e) => setForm({ ...form, effective_to: e.target.value, no_expiry: false })}
                disabled={form.no_expiry}
                data-testid="co-create-effective-to"
              />
              <label className="flex items-center gap-2 text-[11px] text-[#6F828A] mt-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.no_expiry}
                  onChange={(e) => setForm({ ...form, no_expiry: e.target.checked, effective_to: e.target.checked ? "" : form.effective_to })}
                  data-testid="co-create-no-expiry"
                />
                No expiry date
              </label>
            </div>
          </div>

          <div>
            <Label>Review date (optional)</Label>
            <Input
              type="date"
              value={form.review_date}
              onChange={(e) => setForm({ ...form, review_date: e.target.value })}
              data-testid="co-create-review-date"
            />
            <p className="text-[11px] text-[#6F828A] mt-1">
              Reminder date — nothing auto-happens; the app surfaces a nudge.
            </p>
          </div>

          {/* Iter 127 — Explicit supersede picker. Only same-subject
              active/scheduled orders show up. Leaving blank = additive
              order (no replacement). */}
          {form.subject && supersedeCandidates.length > 0 ? (
            <div className="border border-[#E6CFA2] bg-[#FBF3E0] rounded-sm p-3 space-y-2" data-testid="co-create-supersede-block">
              <div className="text-sm font-medium text-[#6F4E1E]">
                Does this replace or amend an existing order?
              </div>
              <div className="text-[11px] text-[#6F4E1E]">
                Only same-subject active/draft orders are listed. Leave blank if this order is additive.
              </div>
              <Select
                value={form.supersedes_order_id || "__none__"}
                onValueChange={(v) => setForm({ ...form, supersedes_order_id: v === "__none__" ? "" : v })}
              >
                <SelectTrigger data-testid="co-create-supersede-select">
                  <SelectValue placeholder="No — this is an additive order" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No — this is an additive order</SelectItem>
                  {supersedeCandidates.map((o) => (
                    <SelectItem key={o.id} value={o.id} data-testid={`co-create-supersede-opt-${o.id}`}>
                      {o.name} ({o.effective_from}
                      {o.effective_to ? ` → ${o.effective_to}` : " — ongoing"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.supersedes_order_id ? (
                <Input
                  value={form.supersede_note}
                  onChange={(e) => setForm({ ...form, supersede_note: e.target.value })}
                  placeholder="Reason for supersession (optional)"
                  data-testid="co-create-supersede-note"
                />
              ) : null}
            </div>
          ) : null}

          {/* Child scope */}
          <div className="border border-[#D9E1E3] rounded-sm p-3 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">All children in this relationship</div>
                <div className="text-[11px] text-[#6F828A]">
                  Off to pick a subset (e.g. only Mia).
                </div>
              </div>
              <Switch
                checked={form.all_children}
                onCheckedChange={(v) => setForm({ ...form, all_children: v })}
                data-testid="co-create-all-children-switch"
              />
            </div>
            {!form.all_children ? (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {(relationship.children || []).length === 0 ? (
                  <p className="text-[11px] text-[#6F828A]">
                    No children are linked to this relationship yet.
                  </p>
                ) : (
                  (relationship.children || []).map((c) => {
                    const on = form.child_ids.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => toggleChild(c.id)}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                          on
                            ? "border-[#1F9FA4] bg-[#E0F2F2] text-[#0F5658]"
                            : "border-[#D9E1E3] bg-white text-[#6F828A] hover:bg-[#F4FAFA]"
                        }`}
                        data-testid={`co-create-child-${c.id}`}
                        aria-pressed={on}
                      >
                        {c.name}
                      </button>
                    );
                  })
                )}
              </div>
            ) : null}
          </div>

          <div>
            <Label>Notes (optional)</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Any context, clauses, or comments."
              rows={3}
              data-testid="co-create-notes"
            />
          </div>

          <div className="flex items-center justify-between border border-[#D9E1E3] rounded-sm p-3">
            <div>
              <div className="text-sm font-medium">Activate immediately</div>
              <div className="text-[11px] text-[#6F828A]">
                Only replaces the specific order you picked above. Other orders stay active.
              </div>
            </div>
            <Switch
              checked={form.activate_now}
              onCheckedChange={(v) => setForm({ ...form, activate_now: v })}
              data-testid="co-create-activate-now"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="bg-[#1F9FA4] hover:bg-[#178085]"
              data-testid="co-create-submit"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              {busy ? "Saving…" : "Create order"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CourtOrderCreateDialog;
